import { Button, ButtonProps, Color, FormField, Spacing, Text, TextInput } from 'zens-storybook'
import { observer } from 'mobx-react-lite'
import React, { ComponentType, ReactElement, useEffect, useState } from 'react'
import { StyleProp, StyleSheet, TextStyle, View, ViewProps, ViewStyle } from 'react-native'
import { AnySchema, ValidationError } from 'yup'
import { get } from 'lodash'
import { Draft } from 'zens-store'
import { useTranslation } from 'react-i18next'
import { Panel } from '@dms/component'
import { Href, Link } from 'zens-router'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    zIndex: 100,
  },
  field: {
    marginBottom: Spacing.XS,
    zIndex: 100,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    maxWidth: 500,
  },
  split: {
    flexDirection: 'row',
    zIndex: 100,
  },
  splitField: {
    flex: 1,
  },
  splitSpace: {
    marginLeft: Spacing.S,
  },
  bottom: {
    marginTop: Spacing.L,
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
    justifyContent: 'space-between',
  },
  buttons: {
    flexDirection: 'row',
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  optional: {
    color: Color.calmGrey,
  },
  divider: {
    backgroundColor: Color.calmGrey,
    marginTop: Spacing.XS,
    marginBottom: Spacing.XL,
    height: 1,
  },
  group: {
    zIndex: 100,
  },
  error: {
    marginBottom: 34,
  },
})

interface Group extends FieldConfig {
  label?: string
  style?: ViewStyle
  dividerStyle?: StyleProp<ViewStyle>
}

interface Field {
  key: string
  errorKeys?: string[]
  label?: string | ReactElement
  help?: string | ReactElement
  style?: StyleProp<ViewStyle>
  labelStyle?: StyleProp<TextStyle>
  inputStyle?: StyleProp<ViewStyle>
  optional?: boolean
  FieldInputComponent?: ComponentType<any>
  fieldProps?: any
  left?: ReactElement
  right?: ReactElement
}

export type FieldConfig = {
  fields: (Field | Field[])[]
}

export type GroupConfig = {
  groups: Group[]
}

type Config = FieldConfig | GroupConfig

interface ButtonConfig extends Omit<ButtonProps, 'onPress'> {
  validate?: boolean
  onPress?: (draft: Draft<any>) => Promise<void>
  href?: Href
}

type Buttons = (ButtonConfig | ButtonConfig[])[]

export interface Props extends ViewProps {
  config: Config
  schema?: AnySchema
  draft: Draft<any>
  buttons?: Buttons
  errors?: Record<string, string>
  onError?: (errors: Record<string, string>) => void
}

const FormView = (props: Props) => {
  const { config, schema, draft, buttons, errors, onError, style, ...otherProps } = props
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>(errors || {})
  const [loading, setLoading] = useState(false)

  const { t } = useTranslation()
  useEffect(() => {
    setFieldErrors(errors || {})
  }, [errors])

  const update = (key: string) => (value: unknown) => {
    draft.update(key, value)
  }

  const validation = async (callback?: () => Promise<void>) => {
    try {
      const data = await schema?.validate(draft.data, { abortEarly: false })
      Object.keys(data).forEach((key) => draft.update(key, data[key]))
    } catch (error) {
      const { inner } = error as ValidationError
      console.debug('FORM: Validation error:', error)
      const newErrors = inner?.reduce((acc, validationError) => {
        const { path, message } = validationError
        return { ...acc, [path!]: message }
      }, {})
      setFieldErrors(newErrors)
      onError?.(newErrors)
      return
    }
    setFieldErrors({})

    callback && callback()
  }
  const handlePress = (onPress?: (draft: Draft<any>) => Promise<void>) => async () => {
    setLoading(true)
    await onPress?.(draft)
    setLoading(false)
  }

  const renderField = (field: Field, fieldStyle?: StyleProp<ViewStyle>) => {
    const {
      label,
      key,
      errorKeys,
      help,
      style,
      labelStyle,
      inputStyle,
      optional,
      FieldInputComponent,
      fieldProps,
      left,
      right,
    } = field
    const fieldError = fieldErrors[key] || errorKeys?.map((errorKey) => fieldErrors[errorKey]).find((error) => !!error)
    const errorStyle = !fieldError && styles.error
    const value = get(draft.data, key)
    return (
      <FormField
        key={key}
        label={
          optional ? (
            <Text level={5}>
              {label} <Text style={styles.optional}>({t('optional')})</Text>
            </Text>
          ) : (
            label
          )
        }
        labelStyle={labelStyle}
        help={help}
        error={fieldError}
        style={[styles.field, fieldStyle, errorStyle, style]}
      >
        <View style={styles.inputRow}>
          {left}
          {FieldInputComponent ? (
            <FieldInputComponent
              error={!!fieldError}
              style={[styles.input, inputStyle]}
              value={value !== undefined ? value : ''}
              onChange={update(key)}
              {...fieldProps}
            />
          ) : (
            <TextInput
              error={!!fieldError}
              style={[styles.input, inputStyle]}
              value={value !== undefined ? value : ''}
              onChangeText={update(key)}
              testID="Form.TextInput"
              {...fieldProps}
            />
          )}
          {right}
        </View>
      </FormField>
    )
  }

  const renderButton = (button: ButtonConfig, index: number) => {
    const { validate, onPress, href, style, ...otherButtonProps } = button

    if (href) {
      return (
        <Link href={href} asChild key={`button-${index}`} style={[styles.button, style]}>
          <Button
            size="L"
            onPress={async () => (validate ? await validation(handlePress(onPress)) : handlePress(onPress)())}
            disabled={loading}
            testID="Form.Button"
            {...otherButtonProps}
          />
        </Link>
      )
    }

    return (
      <Button
        key={`button-${index}`}
        size="L"
        style={[styles.button, style]}
        onPress={async () => (validate && schema ? await validation(handlePress(onPress)) : handlePress(onPress)())}
        disabled={loading}
        testID="Form.Button"
        {...otherButtonProps}
      />
    )
  }

  const renderFields = (config: FieldConfig) => {
    return config.fields.map((item, index) => {
      if (Array.isArray(item)) {
        return (
          <View key={item.map((item) => item.key).join('-')} style={[styles.split, styles.input]}>
            {item.map((field, index) => renderField(field, [styles.splitField, index > 0 && styles.splitSpace]))}
          </View>
        )
      }
      return renderField(item, { zIndex: (100 - index) * 100 })
    })
  }

  const renderGroups = (config: GroupConfig) => {
    return config.groups.map((group, index) => (
      <View key={`group-${index}`} style={[styles.group, group.style, { zIndex: (100 - index) * 100 }]}>
        {index > 0 && <View style={[styles.divider, group.dividerStyle]} />}
        <Panel title={group.label}>{renderFields(group)}</Panel>
      </View>
    ))
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        {config.hasOwnProperty('groups') ? renderGroups(config as GroupConfig) : renderFields(config as FieldConfig)}
      </View>
      {buttons && buttons.length > 0 && (
        <View style={styles.bottom}>
          {buttons?.map((item, index) => {
            if (Array.isArray(item)) {
              return (
                <View key={item.map((item) => item.title).join('-')} style={styles.buttons}>
                  {item.map(renderButton)}
                </View>
              )
            }
            return renderButton(item, index)
          })}
        </View>
      )}
    </View>
  )
}

interface SkeletonProps extends ViewProps {
  config: Config
  numberOfbuttons?: number | number[]
}
FormView.Skeleton = (props: SkeletonProps) => {
  const { config, numberOfbuttons, style, ...otherProps } = props

  const renderField = (field: Field) => {
    const { key, label, FieldInputComponent, style, labelStyle, inputStyle, fieldProps } = field
    return (
      <FormField.Skeleton label={label} style={[styles.field, styles.error, style]} labelStyle={labelStyle} key={key}>
        <View style={styles.inputRow}>
          {FieldInputComponent ? (
            <FieldInputComponent style={[styles.input, inputStyle]} {...fieldProps} />
          ) : (
            <TextInput.Skeleton style={[styles.input, inputStyle]} {...fieldProps} />
          )}
        </View>
      </FormField.Skeleton>
    )
  }

  const renderFields = (config: FieldConfig) => {
    return config.fields.map((item) => {
      if (Array.isArray(item)) {
        return (
          <View key={item.map((item) => item.key).join('-')} style={[styles.split, styles.input]}>
            {item.map((field) => renderField(field))}
          </View>
        )
      }
      return renderField(item)
    })
  }

  const renderGroups = (config: GroupConfig) => {
    return config.groups.map((group, index) => (
      <View key={`group-${index}`} style={[styles.group, group.style, { zIndex: (100 - index) * 100 }]}>
        {index > 0 && <View style={[styles.divider, group.dividerStyle]} />}
        <Panel title={group.label}>{renderFields(group)}</Panel>
      </View>
    ))
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        {config.hasOwnProperty('groups') ? renderGroups(config as GroupConfig) : renderFields(config as FieldConfig)}
      </View>
      {numberOfbuttons && (
        <View style={styles.bottom}>
          {(Array.isArray(numberOfbuttons) ? numberOfbuttons : [numberOfbuttons]).map((item, index) =>
            item > 1 ? (
              <View key={`group-${index}`} style={styles.buttons}>
                {Array(item)
                  .fill(0)
                  .map((item, index) => (
                    <Button.Skeleton key={`button-${index}`} style={styles.button} />
                  ))}
              </View>
            ) : (
              <Button.Skeleton key={`group-${index}`} style={styles.button} />
            ),
          )}
        </View>
      )}
    </View>
  )
}

export default observer(FormView)
